import React, { useState, useEffect } from 'react';
import { positionApi } from '../../api/positionApi';
import { Position, CreatePositionRequest } from '../../types/position.type';
import { Worker } from '../../types/worker.type';
import { Button, Card, Form, Input, Modal, Select, Spin, Tag, Typography, Space, message, InputNumber } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, DragOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/auth.context';
import useGetRequest from '../../hooks/useGetRequest';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { Title, Text } = Typography;

interface WorkerOption {
    value: string;
    label: string;
}

interface FormShift {
    shiftName: string;
    workerIds: WorkerOption[];
}

interface PositionFormData {
    name: string;
    description?: string;
    shifts: FormShift[];
    additionalWorkers: WorkerOption[];
    requiredWorkersCount: number;
}

interface SortablePositionCardProps {
    position: Position;
    onEdit: (position: Position) => void;
    onDelete: (id: string) => void;
    calculateMissingWorkers: (position: Position) => number;
}

const SortablePositionCard: React.FC<SortablePositionCardProps> = ({
    position,
    onEdit,
    onDelete,
    calculateMissingWorkers,
}) => {
    const { authState } = useAuth();
    const isAdmin = authState.user?.role === 'admin';
    
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: position._id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const missingWorkers = calculateMissingWorkers(position);

    return (
        <div ref={setNodeRef} style={style}>
            <Card
                style={{
                    marginBottom: 12,
                    border: isDragging ? '2px dashed #1890ff' : undefined,
                }}
                title={
                    <Space>
                        {isAdmin && (
                            <div {...attributes} {...listeners} style={{ cursor: 'grab' }}>
                                <DragOutlined style={{ color: '#999' }} />
                            </div>
                        )}
                        <Text strong>{position.name}</Text>
                        {missingWorkers > 0 && (
                            <Tag style={{ color: 'red', fontWeight: 'bold' }}>
                                Не хватает {missingWorkers} сотрудников
                            </Tag>
                        )}
                    </Space>
                }
                extra={
                    <Space>
                        <Button icon={<EditOutlined />} onClick={() => onEdit(position)}>
                            Редактировать
                        </Button>
                        <Button danger icon={<DeleteOutlined />} onClick={() => onDelete(position._id)}>
                            Удалить
                        </Button>
                    </Space>
                }
            >
                {position.description && <Text type="secondary">{position.description}</Text>}
                <div style={{ marginTop: 12 }}>
                    <Text strong>Требуемое количество сотрудников: {position.requiredWorkersCount}</Text>
                </div>
                <div style={{ marginTop: 12 }}>
                    <Text strong>Смены:</Text>
                    {position.shifts.map((shift, idx) => (
                        <div key={idx} style={{ margin: '8px 0' }}>
                            <Text>{shift.shiftName}:</Text>
                            <Space wrap>
                                {shift.workerIds.map(workerId => (
                                    <Tag key={workerId._id} color="blue">{workerId.name} {workerId.surname}{workerId.nickname ? ` (${workerId.nickname})` : ''}</Tag>
                                ))}
                            </Space>
                        </div>
                    ))}
                </div>
                {position.additionalWorkers.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                        <Text strong>Дополнительные работники:</Text>
                        <Space wrap>
                            {position.additionalWorkers.map(workerId => (
                                <Tag key={workerId._id} color="purple">{workerId.name} {workerId.surname}{workerId.nickname ? ` (${workerId.nickname})` : ''}</Tag>
                            ))}
                        </Space>
                    </div>
                )}
            </Card>
        </div>
    );
};

const PositionsPage: React.FC = () => {
    const [positions, setPositions] = useState<Position[]>([]);
    const [workers, setWorkers] = useState<Worker[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingPosition, setEditingPosition] = useState<Position | null>(null);
    const [form] = Form.useForm<any>();
    const { authState } = useAuth();
    const { data: workerList } = useGetRequest("/worker/list")
    const isAdmin = authState.user?.role === 'admin';

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [positionsData, workersData] = await Promise.all([
                positionApi.getPositions(),
                positionApi.getWorkersForPosition(),
            ]);
            setPositions(positionsData);
            setWorkers(workersData);
        } catch (error) {
            message.error('Ошибка при загрузке данных');
        } finally {
            setLoading(false);
        }
    };

    const openModal = (position?: Position) => {
        setEditingPosition(position || null);
        if (position) {
            form.setFieldsValue({
                name: position.name,
                description: position.description || '',
                shifts: position.shifts.map(shift => ({
                    shiftName: shift.shiftName,
                    workerIds: shift.workerIds.map((worker: any) => ({
                        value: worker._id,
                        label: `${worker.name} ${worker.surname}${worker.nickname ? ` (${worker.nickname})` : ''}`
                    }))
                })),
                additionalWorkers: position.additionalWorkers.map((worker:any) => ({
                    value: worker._id,
                    label: `${worker.name} ${worker.surname}${worker.nickname ? ` (${worker.nickname})` : ''}`
                })),
                requiredWorkersCount: position.requiredWorkersCount,
            });
        } else {
            form.resetFields();
        }
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingPosition(null);
        form.resetFields();
    };

    const handleSubmit = async (values: CreatePositionRequest) => {
        setSubmitting(true)
        try {
            // Преобразуем данные из labelInValue формата в обычный формат для отправки
            const processedValues = {
                ...values,
                shifts: values.shifts?.map(shift => ({
                    shiftName: shift.shiftName,
                    workerIds: shift.workerIds?.map((worker: any) => 
                        typeof worker === 'string' ? worker : worker.value
                    ) || []
                })) || [],
                additionalWorkers: values.additionalWorkers?.map((worker: any) => 
                    typeof worker === 'string' ? worker : worker.value
                ) || []
            };

            if (editingPosition) {
                await positionApi.updatePosition(editingPosition._id, processedValues);
                message.success('Должность обновлена');
            } else {
                await positionApi.createPosition(processedValues);
                message.success('Должность создана');
            }
            closeModal();
            loadData();
        } catch (error: any) {
            message.error(error.response?.data?.error || 'Произошла ошибка');
        } finally {
            setSubmitting(false)
        }
    };

    const handleDelete = async (id: string) => {
        Modal.confirm({
            title: 'Удалить должность?',
            content: 'Вы уверены, что хотите удалить эту должность?',
            okText: 'Удалить',
            cancelText: 'Отмена',
            okType: 'danger',
            onOk: async () => {
                try {
                    await positionApi.deletePosition(id);
                    message.success('Должность удалена');
                    loadData();
                } catch (error: any) {
                    message.error(error.response?.data?.error || 'Ошибка при удалении');
                }
            },
        });
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setPositions((items) => {
                const oldIndex = items.findIndex((item) => item._id === active.id);
                const newIndex = items.findIndex((item) => item._id === over?.id);

                const newPositions = arrayMove(items, oldIndex, newIndex);
                
                // Update sort order in backend
                const updatedPositions = newPositions.map((position, index) => ({
                    id: position._id,
                    sortOrder: index,
                }));

                // Send update to backend
                positionApi.updateSortOrder(updatedPositions).catch((error) => {
                    message.error('Ошибка при обновлении порядка сортировки');
                    console.error('Error updating sort order:', error);
                });

                return newPositions;
            });
        }
    };

 

    const calculateMissingWorkers = (position: Position) => {
        const totalAssignedWorkers = position.shifts.reduce((total, shift) => {
            return total + shift.workerIds.length;
        }, 0) + position.additionalWorkers.length;

        const missingCount = position.requiredWorkersCount - totalAssignedWorkers;
        return Math.max(0, missingCount);
    };

    if (loading) {
        return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
    }

    const renderPositions = () => (
        <Space direction="vertical" style={{ width: '100%' }} size={24}>
            {positions.map(position => (
                <SortablePositionCard
                    key={position._id}
                    position={position}
                    onEdit={openModal}
                    onDelete={handleDelete}
                    calculateMissingWorkers={calculateMissingWorkers}
                />
            ))}
        </Space>
    );

    return (
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 24 }}>
                <Title level={2}>Управление должностями</Title>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
                    Создать должность
                </Button>
            </Space>

            <Modal
                open={modalOpen}
                title={editingPosition ? 'Редактировать должность' : 'Создать должность'}
                onCancel={closeModal}
                footer={null}
                destroyOnClose
                width={700}
                confirmLoading={submitting}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{ shifts: [], additionalWorkers: [], requiredWorkersCount: 1 }}
                >
                    <Form.Item
                        name="name"
                        label="Название должности"
                        rules={[{ required: true, message: 'Введите название' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Описание"
                    >
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item
                        name="requiredWorkersCount"
                        label="Требуемое количество сотрудников"
                        rules={[{ required: true, message: 'Введите количество' }]}
                    >
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.List name="shifts">
                        {(fields, { add, remove }) => (
                            <div>
                                <Text strong>Смены:</Text>
                                {fields.map((field, idx) => (
                                    <Card
                                        key={field.name}
                                        size="small"
                                        style={{ marginBottom: 12 }}
                                        title={
                                            <Form.Item
                                                {...field}
                                                name={[field.name, 'shiftName']}
                                                rules={[{ required: true, message: 'Название смены' }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Input placeholder="Название смены" />
                                            </Form.Item>
                                        }
                                        extra={
                                            <Button danger type="text" icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                                        }
                                    >
                                        <Form.Item
                                            name={[field.name, 'workerIds']}
                                            label="Работники в смене"
                                            rules={[{ required: true, message: 'Выберите работников' }]}
                                        >
                                            <Select
                                                mode="multiple"
                                                placeholder="Выберите работников"
                                                optionFilterProp="children"
                                                showSearch
                                                maxTagCount={4}
                                                labelInValue
                                            >
                                                {workers.map(worker => (
                                                    <Select.Option key={worker._id} value={worker._id}>
                                                       {worker.name} {worker.surname} {worker.nickname ? ` (${worker.nickname})` : ''}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Card>
                                ))}
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Добавить смену</Button>
                            </div>
                        )}
                    </Form.List>
                    <Form.Item name="additionalWorkers" label="Дополнительные работники">
                        <Select
                            mode="multiple"
                            placeholder="Выберите дополнительных работников"
                            optionFilterProp="children"
                            showSearch
                            maxTagCount={4}
                            labelInValue
                        >
                            {workers.map(worker => (
                                <Select.Option key={worker._id} value={worker._id}>
                                    {worker.name} {worker.surname} {worker.nickname ? ` (${worker.nickname})` : ''}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {editingPosition ? 'Обновить' : 'Создать'}
                            </Button>
                            <Button onClick={closeModal}>Отмена</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {isAdmin ? (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={positions.map(pos => pos._id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {renderPositions()}
                    </SortableContext>
                </DndContext>
            ) : (
                renderPositions()
            )}
        </div>
    );
};

export default PositionsPage; 